import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeLatest } from 'redux-saga/effects';
import { ApiResult } from 'common/models';
import { UserService } from './user.service';
import { userActions } from './user.slice';
import { IAsyncResponseStatus } from 'common/models/store/store.model';
import { DeleteUserRoleApiPayload, UserPermission } from '../models/user.model';
import { appStateActions } from 'common/redux/app-state';
import { DEFAULT_ERROR_MESSAGE, FAILURE_HEADER, SUCCESS_HEADER } from 'common/config';
import { PERMISSION_EDIT_MESSAGE } from 'common/constants/user';
import { MessageType } from 'common/enums';
import { PERMISSION_DELETE_FAILED, PERMISSION_DELETE_MESSAGE } from '../config/user.config';

/**
 *
 * getUserList
 */
const getUserList = function* () {
    const { hasErrors, value }: ApiResult<IAsyncResponseStatus> = yield UserService.getUserList();
    if (hasErrors) {
        yield put({
            type: userActions.getUserListError.type,
            payload: value
        });
    } else {
        yield put({
            type: userActions.getUserListSuccess.type,
            payload: value
        });
    }
};

const getUserListWatch = function* () {
    yield takeLatest(userActions.getUserList.type, getUserList);
};

/**
 *
 * deletePermission
 */
const deletePermission = function* ({
    payload: { data, onSuccess }
}: PayloadAction<{ data: DeleteUserRoleApiPayload; onSuccess: (param: boolean) => void }>) {
    const { hasErrors, value }: ApiResult<IAsyncResponseStatus> = yield UserService.deleteRole(
        data
    );
    if (hasErrors || !value) {
        yield put({
            type: userActions.deletePermissionError.type,
            payload: hasErrors ? DEFAULT_ERROR_MESSAGE : PERMISSION_DELETE_FAILED
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: FAILURE_HEADER,
                message: hasErrors ? DEFAULT_ERROR_MESSAGE : PERMISSION_DELETE_FAILED,
                type: MessageType.Error
            }
        });
    } else if (value) {
        yield call(onSuccess, true);
        yield put({
            type: userActions.deletePermissionSuccess.type,
            payload: value
        });
        yield put({
            type: appStateActions.showMessageModal.type,
            payload: {
                isVisible: true,
                header: SUCCESS_HEADER,
                message: PERMISSION_DELETE_MESSAGE,
                type: MessageType.Success
            }
        });
    }
};

const deleteRoleWatch = function* () {
    yield takeLatest(userActions.deletePermission.type, deletePermission);
};

/**
 *
 * assignRole
 */
const assignRole = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, value }: ApiResult<IAsyncResponseStatus> = yield UserService.assignRole(
        payload
    );
    if (hasErrors) {
        yield put({
            type: userActions.assignRoleSuccess.type,
            payload: value
        });
    } else {
        yield put({
            type: userActions.assignRoleError.type,
            payload: value
        });
    }
};

const assignRoleWatch = function* () {
    yield takeLatest(userActions.assignRole.type, assignRole);
};

/**
 *
 * editRole
 */
const editRole = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, value }: ApiResult<Array<IAsyncResponseStatus>> = yield UserService.editRole(
        payload
    );
    if (hasErrors) {
        yield put({
            type: userActions.editRoleSuccess.type,
            payload: value
        });
    } else {
        yield put({
            type: userActions.editRoleError.type,
            payload: value
        });
    }
};

const editRoleWatch = function* () {
    yield takeLatest(userActions.editRole.type, editRole);
};

/**
 *
 * getRoleList
 */
const getRolesList = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, value }: ApiResult<any> = yield UserService.getRoleList(payload);
    if (hasErrors) {
        yield put({
            type: userActions.getRolesListSuccess.type,
            payload: value
        });
    } else {
        yield put({
            type: userActions.getRolesListError.type,
            payload: value
        });
    }
};

const getRoleListWatch = function* () {
    yield takeLatest(userActions.getRolesList.type, getRolesList);
};

/**
 *
 * getPermissionList
 */
const getPermissionList = function* ({ payload }: PayloadAction<any>) {
    const { hasErrors, value }: ApiResult<{ data: Array<UserPermission> }> =
        yield UserService.getUserRoles(payload);
    if (hasErrors) {
        yield put({
            type: userActions.getPermissionListError.type,
            payload: value
        });
    } else {
        yield put({
            type: userActions.getPermissionListSuccess.type,
            payload: value
        });
    }
};

const getPermissionListWatch = function* () {
    yield takeLatest(userActions.getPermissionList.type, getPermissionList);
};

export {
    getUserListWatch,
    deleteRoleWatch,
    assignRoleWatch,
    editRoleWatch,
    getRoleListWatch,
    getPermissionListWatch
};
